import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { getCurrentUserProfileObjectId } from '../../utils';
import {
  formatCampaignMoney,
  formatCampaignRatePer1k,
  normalizeCampaignCurrency,
} from '../../utils/campaignCurrency';
import './CuratorEarningsPage.css';
import { getSubmissionBackendUrl } from '../../constants/api-urls';

type RangeKey = 'all' | '30d' | '90d' | 'this_month';

interface CurrencySummary {
  potentialEarnings: number;
  acceptedEarnings: number;
  completedEarnings: number;
  thisMonthEarnings: number;
  totalRequestsReceived: number;
  totalRequestsAccepted: number;
  totalRequestsRejected: number;
  activeRequests: number;
}

interface TimelineItem {
  month: string;
  acceptedEarnings: number;
  completedEarnings: number;
  wonRequests: number;
}

interface HistoryItem {
  requestId: string;
  campaignId: string;
  campaignName: string;
  artistName: string;
  currencyCode: string;
  mode: 'single' | 'split';
  status: string;
  rateSource: string;
  overrideReasonType: string;
  requestedStreams: number;
  offeredRatePer1k: number;
  offeredPayout: number;
  acceptedRatePer1k: number;
  acceptedPayout: number;
  won: boolean;
  rejectedByCurator: boolean;
  isCompetitive: boolean;
  assignedAt?: string;
  acceptedAt?: string;
}

interface PaginationState {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface EarningsPayload {
  range: RangeKey;
  summaryByCurrency: Record<string, CurrencySummary>;
  statusBreakdown: Record<string, number>;
  monthlyTimelineByCurrency: Record<string, TimelineItem[]>;
  currenciesPresent: string[];
  history: HistoryItem[];
  pagination: PaginationState;
}

const RANGE_OPTIONS: Array<{ key: RangeKey; label: string }> = [
  { key: 'all', label: 'All time' },
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
  { key: 'this_month', label: 'This month' },
];

const STATUS_GROUPS: Array<{ key: string; label: string; statuses: string[] }> = [
  { key: 'available', label: 'Available to accept', statuses: ['new', 'pending', 'in-review'] },
  { key: 'live', label: 'Accepted', statuses: ['accepted', 'in-progress', 'partially-completed'] },
  { key: 'scheduled', label: 'Accepted, starting soon', statuses: ['scheduled'] },
  { key: 'completed', label: 'Completed', statuses: ['completed'] },
  { key: 'rejected', label: 'Rejected', statuses: ['rejected'] },
];

const toFiniteNumber = (value: unknown): number => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatDateTime = (value?: string): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const formatMonth = (monthKey: string): string => {
  if (!monthKey || !monthKey.includes('-')) return monthKey;
  const [year, month] = monthKey.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) return monthKey;
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

const toKebabToken = (value: string): string =>
  (value || 'unknown').toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');

const getDisplayStatusLabel = (status?: string): string => {
  switch ((status || '').toLowerCase()) {
    case 'new':
    case 'pending':
    case 'in-review':
      return 'Available to accept';
    case 'accepted':
      return 'Accepted';
    case 'scheduled':
      return 'Accepted, starting soon';
    case 'in-progress':
    case 'partially-completed':
      return 'Accepted';
    case 'completed':
      return 'Completed';
    case 'rejected':
      return 'Rejected';
    default:
      return status || 'Unknown';
  }
};

const defaultPayload: EarningsPayload = {
  range: 'all',
  summaryByCurrency: {},
  statusBreakdown: {},
  monthlyTimelineByCurrency: {},
  currenciesPresent: [],
  history: [],
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  },
};

const CuratorEarningsPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedRange, setSelectedRange] = useState<RangeKey>('all');
  const [data, setData] = useState<EarningsPayload>(defaultPayload);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingPage, setLoadingPage] = useState<boolean>(false);

  const fetchDashboard = useCallback(
    async (range: RangeKey, page = 1, pageLoad = false) => {
      const submissionBackendUrl = getSubmissionBackendUrl();
      console.log("submissionBackendUrl", submissionBackendUrl)
      if (!submissionBackendUrl) {
        setError('Submission backend URL is missing. Please configure REACT_APP_BACKEND_SUBMISSION_URI.');
        setLoading(false);
        return;
      }

      if (pageLoad) {
        setLoadingPage(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const userProfileObjectId = await getCurrentUserProfileObjectId();
        if (!userProfileObjectId) {
          throw new Error('Unable to resolve current curator profile.');
        }

        const response = await axios.get(
          `${submissionBackendUrl}/curatorCampaign/earnings/${userProfileObjectId}`,
          {
            params: {
              range,
              page,
              limit: 20,
            },
          }
        );

        setData(response?.data?.data || defaultPayload);
      } catch (fetchError: any) {
        const status = fetchError?.response?.status;
        if (status === 404) {
          setError('Earnings dashboard feature is currently disabled.');
        } else {
          setError(fetchError?.response?.data?.message || fetchError?.message || 'Failed to load earnings dashboard.');
        }
      } finally {
        if (pageLoad) {
          setLoadingPage(false);
        } else {
          setLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    fetchDashboard(selectedRange, 1);
  }, [fetchDashboard, selectedRange]);

  const orderedStatusRows = useMemo(() => {
    const breakdown = data?.statusBreakdown || {};
    const groupedRows = STATUS_GROUPS.map(group => {
      const count = group.statuses.reduce(
        (sum, status) => sum + toFiniteNumber(breakdown[status]),
        0
      );
      return {
        status: group.key,
        label: group.label,
        count,
      };
    }).filter(row => row.count > 0);

    const total = groupedRows.reduce((sum, row) => sum + row.count, 0);

    return groupedRows.map(row => ({
      ...row,
      percentage: total > 0 ? (row.count / total) * 100 : 0,
    }));
  }, [data?.statusBreakdown]);

  const currenciesPresent = useMemo(() => {
    const explicitCurrencies = (data?.currenciesPresent || []).map(normalizeCampaignCurrency);
    if (explicitCurrencies.length > 0) {
      return Array.from(new Set(explicitCurrencies));
    }
    return Object.keys(data?.summaryByCurrency || {}).map(normalizeCampaignCurrency);
  }, [data?.currenciesPresent, data?.summaryByCurrency]);

  const currencySummarySections = useMemo(() => {
    return currenciesPresent.map(currencyCode => {
      const summary = data?.summaryByCurrency?.[currencyCode] || {
        potentialEarnings: 0,
        acceptedEarnings: 0,
        completedEarnings: 0,
        thisMonthEarnings: 0,
        totalRequestsReceived: 0,
        totalRequestsAccepted: 0,
        totalRequestsRejected: 0,
        activeRequests: 0,
      };

      return {
        currencyCode,
        summary,
        metrics: [
          { label: 'Potential earnings', value: formatCampaignMoney(summary.potentialEarnings, currencyCode), tone: 'default' },
          { label: 'Won earnings', value: formatCampaignMoney(summary.acceptedEarnings, currencyCode), tone: 'primary' },
          { label: 'Completed earnings', value: formatCampaignMoney(summary.completedEarnings, currencyCode), tone: 'success' },
          { label: 'This month', value: formatCampaignMoney(summary.thisMonthEarnings, currencyCode), tone: 'default' },
          { label: 'Won requests', value: summary.totalRequestsAccepted.toString(), tone: 'primary' },
          { label: 'Active requests', value: summary.activeRequests.toString(), tone: 'default' },
        ],
      };
    });
  }, [currenciesPresent, data?.summaryByCurrency]);

  const monthlySections = useMemo(() => {
    return currenciesPresent.map(currencyCode => {
      const timeline = data?.monthlyTimelineByCurrency?.[currencyCode] || [];
      const maxAccepted = Math.max(...timeline.map(item => toFiniteNumber(item.acceptedEarnings)), 0);

      return {
        currencyCode,
        items: timeline.map(item => {
          const accepted = toFiniteNumber(item.acceptedEarnings);
          const completed = toFiniteNumber(item.completedEarnings);
          return {
            ...item,
            acceptedHeight: maxAccepted > 0 ? Math.max(8, Math.round((accepted / maxAccepted) * 120)) : 8,
            completedHeight: maxAccepted > 0 ? Math.max(6, Math.round((completed / maxAccepted) * 120)) : 6,
          };
        }),
      };
    });
  }, [currenciesPresent, data?.monthlyTimelineByCurrency]);

  if (loading) {
    return (
      <div className="earnings-dashboard">
        <LoadingSpinner text="Loading earnings dashboard..." />
      </div>
    );
  }

  return (
    <div className="earnings-dashboard">
      <div className="earnings-dashboard__header">
        <div>
          <h2>Earnings Dashboard</h2>
          <p>Track open requests, won payouts, and completed earnings by campaign currency.</p>
        </div>
        <div className="earnings-dashboard__header-actions">
          <button
            type="button"
            className="earnings-dashboard__link-button"
            onClick={() => navigate('/campaigns')}
          >
            View campaigns
          </button>
        </div>
      </div>

      {error ? (
        <div className="earnings-dashboard__error">
          <p>{error}</p>
          <button type="button" onClick={() => fetchDashboard(selectedRange, data.pagination.page || 1)}>
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="earnings-dashboard__range">
            {RANGE_OPTIONS.map(option => (
              <button
                key={option.key}
                type="button"
                className={selectedRange === option.key ? 'active' : ''}
                onClick={() => setSelectedRange(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="earnings-dashboard__currency-groups">
            {currencySummarySections.length === 0 ? (
              <div className="analytics-card">
                <p className="analytics-empty">No earnings data yet for this range.</p>
              </div>
            ) : (
              currencySummarySections.map(section => (
                <section key={section.currencyCode} className="currency-summary-card">
                  <div className="currency-summary-card__header">
                    <div>
                      <h3>{section.currencyCode}</h3>
                      <p>{section.summary.totalRequestsReceived} tracked requests</p>
                    </div>
                    <span className="currency-summary-card__tag">Campaign currency</span>
                  </div>
                  <div className="earnings-dashboard__kpis">
                    {section.metrics.map(metric => (
                      <article key={`${section.currencyCode}-${metric.label}`} className={`kpi-card kpi-card--${metric.tone}`}>
                        <h4>{metric.label}</h4>
                        <p>{metric.value}</p>
                      </article>
                    ))}
                  </div>
                </section>
              ))
            )}
          </div>

          <div className="earnings-dashboard__analytics">
            <section className="analytics-card">
              <h3>Current request statuses</h3>
              {orderedStatusRows.length === 0 ? (
                <p className="analytics-empty">No status data for this range.</p>
              ) : (
                <ul className="status-list">
                  {orderedStatusRows.map(row => (
                    <li key={row.status}>
                      <div className="status-list__label">
                        <span>{row.label}</span>
                        <strong>{row.count}</strong>
                      </div>
                      <div className="status-list__bar">
                        <div style={{ width: `${row.percentage}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {monthlySections.map(section => (
              <section key={section.currencyCode} className="analytics-card">
                <div className="analytics-card__header">
                  <h3>Monthly payout trend ({section.currencyCode})</h3>
                  <div className="analytics-legend">
                    <span><i className="analytics-legend__dot analytics-legend__dot--won" /> Won</span>
                    <span><i className="analytics-legend__dot analytics-legend__dot--completed" /> Completed</span>
                  </div>
                </div>
                {section.items.length === 0 ? (
                  <p className="analytics-empty">No monthly earnings yet for {section.currencyCode}.</p>
                ) : (
                  <div className="monthly-bars">
                    {section.items.map(item => (
                      <div key={`${section.currencyCode}-${item.month}`} className="monthly-bars__item">
                        <div className="monthly-bars__bars">
                          <span
                            className="monthly-bars__bar monthly-bars__bar--accepted"
                            style={{ height: `${item.acceptedHeight}px` }}
                            title={`Won: ${formatCampaignMoney(item.acceptedEarnings, section.currencyCode)}`}
                          />
                          <span
                            className="monthly-bars__bar monthly-bars__bar--completed"
                            style={{ height: `${item.completedHeight}px` }}
                            title={`Completed: ${formatCampaignMoney(item.completedEarnings, section.currencyCode)}`}
                          />
                        </div>
                        <div className="monthly-bars__meta">
                          <small>{formatMonth(item.month)}</small>
                          <small>{formatCampaignMoney(item.acceptedEarnings, section.currencyCode)}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>

          <section className="earnings-dashboard__history">
            <div className="history-header">
              <h3>Request history</h3>
              {loadingPage ? <span>Loading page...</span> : null}
            </div>

            {data.history.length === 0 ? (
              <p className="analytics-empty">No requests found for this range.</p>
            ) : (
              <div className="history-table-wrap">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Campaign</th>
                      <th>Currency</th>
                      <th>Status</th>
                      <th>Streams</th>
                      <th>Rate / 1K</th>
                      <th>Offered</th>
                      <th>Won</th>
                      <th>Won at</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.history.map(row => {
                      const rowCurrencyCode = normalizeCampaignCurrency(row.currencyCode);
                      return (
                        <tr key={row.requestId}>
                          <td>
                            <div className="history-campaign">
                              <strong>{row.campaignName || '-'}</strong>
                              <small>{row.artistName || '-'}</small>
                            </div>
                          </td>
                          <td>{rowCurrencyCode}</td>
                          <td>
                            <span className={`status-chip status-chip--${toKebabToken(row.status)}`}>
                              {getDisplayStatusLabel(row.status)}
                            </span>
                          </td>
                          <td>{toFiniteNumber(row.requestedStreams).toLocaleString()}</td>
                          <td>{formatCampaignRatePer1k(row.offeredRatePer1k, rowCurrencyCode)}</td>
                          <td>{formatCampaignMoney(row.offeredPayout, rowCurrencyCode)}</td>
                          <td>{formatCampaignMoney(row.acceptedPayout, rowCurrencyCode)}</td>
                          <td>{formatDateTime(row.acceptedAt || row.assignedAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="history-pagination">
              <button
                type="button"
                disabled={data.pagination.page <= 1 || loadingPage}
                onClick={() => fetchDashboard(selectedRange, data.pagination.page - 1, true)}
              >
                Previous
              </button>
              <span>
                Page {data.pagination.page} of {data.pagination.totalPages}
              </span>
              <button
                type="button"
                disabled={data.pagination.page >= data.pagination.totalPages || loadingPage}
                onClick={() => fetchDashboard(selectedRange, data.pagination.page + 1, true)}
              >
                Next
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default CuratorEarningsPage;
